import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, DollarSign, Globe } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { feesAPI, nationalitiesAPI, serviceTypesAPI, servicesAPI, subServicesAPI } from '../../utils/api';


const EMPTY_FEE_COMPONENTS = {
  consular_fee: 0,
  icwf_fee: 0,
  service_fee: 0
};


const AdminFees = () => {
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultNationalityId, setDefaultNationalityId] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [nationalityDialogOpen, setNationalityDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  
  const [feeForm, setFeeForm] = useState({
    nationality_id: '',
    service_type_id: '',
    service_id: '',
    sub_service_id: '',
    currency: 'KRW',
    fee_components: { ...EMPTY_FEE_COMPONENTS }
  });
  
  const [nationalityForm, setNationalityForm] = useState({ name: '', code: '' });
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredSubServices, setFilteredSubServices] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (feeForm.service_type_id) {
      setFilteredServices(services.filter(s => s.service_type_id === feeForm.service_type_id));
    } else {
      setFilteredServices([]);
    }
    setFeeForm(prev => ({ ...prev, service_id: '', sub_service_id: '' }));
  }, [feeForm.service_type_id, services]);

  useEffect(() => {
    if (feeForm.service_id) {
      setFilteredSubServices(subServices.filter(ss => ss.service_id === feeForm.service_id));
    } else {
      setFilteredSubServices([]);
    }
    setFeeForm(prev => ({ ...prev, sub_service_id: '' }));
  }, [feeForm.service_id, subServices]);

  const loadData = async () => {
    try {
      const [
        feesRes,
        natRes,
        typesRes,
        servicesRes,
        subServicesRes
      ] = await Promise.all([
        feesAPI.getAll({ include_inactive: true }),
        nationalitiesAPI.getAll(true),
        serviceTypesAPI.getAll(true),
        servicesAPI.getAll(null, true),
        subServicesAPI.getAll(null, true)
      ]);
  
      setFees(feesRes.data);
      setNationalities(natRes.data);
      setServiceTypes(typesRes.data);
      setServices(servicesRes.data);
      setSubServices(subServicesRes.data);
  
      // 🔑 DEFAULT nationality is OPTIONAL
      try {
        const defaultNatRes = await nationalitiesAPI.getDefault();
        if (defaultNatRes?.data?.id) {
          setDefaultNationalityId(defaultNatRes.data.id);
        }
      } catch (err) {
        // 404 is OK here – means "not set yet"
        setDefaultNationalityId('');
      }
  
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFeeSubmit = async (e) => {
    e.preventDefault();
  
    try {
      const payload = {
        nationality_id: feeForm.nationality_id,
        service_type_id: feeForm.service_type_id,
        service_id: feeForm.service_id,
        currency: feeForm.currency,
        fee_components: feeForm.fee_components
      };
  
      if (feeForm.sub_service_id) {
        payload.sub_service_id = feeForm.sub_service_id;
      }
  
      if (editingFee) {
        await feesAPI.update(editingFee.id, payload);
        toast.success('Fee updated');
      } else {
        await feesAPI.create(payload);
        toast.success('Fee created');
      }
  
      setFeeDialogOpen(false);
      resetFeeForm();
      loadData();
  
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleSetDefaultNationality = async (id) => {
    try {
      setSavingDefault(true);
      await nationalitiesAPI.setDefault(id);
      setDefaultNationalityId(id);
      toast.success('Default nationality updated');
    } catch (error) {
      toast.error('Failed to set default nationality');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleEditFee = (fee) => {
    setEditingFee(fee);
  
    setFeeForm({
      nationality_id: fee.nationality_id,
      service_type_id: fee.service_type_id,
      service_id: fee.service_id,
      sub_service_id: fee.sub_service_id || '',
      currency: fee.currency || 'KRW',
  
      fee_components: {
        consular_fee:
          fee?.fee_components?.consular_fee ??
          fee?.consular_fee ??
          0,
  
        icwf_fee:
          fee?.fee_components?.icwf_fee ??
          fee?.icwf_fee ??
          0,
  
        service_fee:
          fee?.fee_components?.service_fee ??
          fee?.service_fee ??
          0,
      }
    });
  
    setFeeDialogOpen(true);
  };

  const handleDeleteFee = async (id) => {
    if (window.confirm('Delete this fee structure?')) {
      try {
        await feesAPI.delete(id);
        toast.success('Fee deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const handleNationalitySubmit = async (e) => {
    e.preventDefault();
    try {
      await nationalitiesAPI.create(nationalityForm);
      toast.success('Nationality added');
      setNationalityDialogOpen(false);
      setNationalityForm({ name: '', code: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteNationality = async (id) => {
    if (window.confirm('Delete this nationality?')) {
      try {
        await nationalitiesAPI.delete(id);
        toast.success('Nationality deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetFeeForm = () => {
    setEditingFee(null);
    setFeeForm({
      nationality_id: '',
      service_type_id: '',
      service_id: '',
      sub_service_id: '',
      currency: 'KRW',
      fee_components: { ...EMPTY_FEE_COMPONENTS }
    });
  };

  const getName = (list, id) => list.find(item => item.id === id)?.name || 'Unknown';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Fee Management
              </h1>
              <p className="text-muted-foreground">
                Configure fees based on nationality, service type, and service
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>
          

          {/* Nationalities Section */}
          <div className="bg-white rounded-lg border shadow-sm p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-accent" />
                <h2 className="text-xl font-semibold">Nationalities</h2>
              </div>
              <Button onClick={() => setNationalityDialogOpen(true)} data-testid="add-nationality-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Nationality
              </Button>
            </div>
            {/* Default Nationality Selector */}
            <div className="mb-4 max-w-sm">
              <Label className="mb-1 block">Default Nationality</Label>
              <Select
                value={defaultNationalityId}
                onValueChange={(value) => handleSetDefaultNationality(value)}
                disabled={savingDefault}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select default nationality" />
                </SelectTrigger>
                <SelectContent>
                  {nationalities
                    .filter(n => n.is_active)
                    .map((nat) => (
                      <SelectItem key={nat.id} value={nat.id}>
                        {nat.name} ({nat.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {savingDefault && (
                <p className="text-sm text-muted-foreground mt-1">
                  Updating default nationality...
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {nationalities.map((nat) => (
                <div key={nat.id} className="flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
                  <span className="font-medium">{nat.name}</span>
                  <span className="text-sm text-muted-foreground">({nat.code})</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => handleDeleteNationality(nat.id)}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Fee Structures Section */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-accent" />
                <h2 className="text-xl font-semibold">Fee Structures</h2>
              </div>
              <Button onClick={() => { resetFeeForm(); setFeeDialogOpen(true); }} data-testid="add-fee-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Fee Structure
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nationality</TableHead>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Sub-Service</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-right">Processing</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fees.map((fee) => {
                    const consular = Number(fee.fee_components?.consular_fee ?? 0);
                    const icwf = Number(fee.fee_components?.icwf_fee ?? 0);
                    const service = Number(fee.fee_components?.service_fee ?? 0);
                    const total = consular + icwf + service;

                    return (
                      <TableRow
                        key={fee.id}
                        className={!fee.is_active ? 'opacity-50' : ''}
                      >
                        <TableCell>
                          {getName(nationalities, fee.nationality_id)}
                        </TableCell>

                        <TableCell>
                          {getName(serviceTypes, fee.service_type_id)}
                        </TableCell>

                        <TableCell>
                          {getName(services, fee.service_id)}
                        </TableCell>

                        <TableCell>
                          {fee.sub_service_id
                            ? getName(subServices, fee.sub_service_id)
                            : '-'}
                        </TableCell>

                        {/* Consular Fee */}
                        <TableCell className="text-right font-mono">
                          {fee.currency} {consular.toFixed(2)}
                        </TableCell>

                        {/* ICWF + Service Fee */}
                        <TableCell className="text-right font-mono">
                          {fee.currency} {(icwf + service).toFixed(2)}
                        </TableCell>

                        {/* Total */}
                        <TableCell className="text-right font-mono font-semibold">
                          {fee.currency} {total.toFixed(2)}
                        </TableCell>

                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditFee(fee)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteFee(fee.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {fees.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No fee structures configured. Add your first fee structure.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fee Dialog */}
      <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingFee ? 'Edit Fee Structure' : 'Add Fee Structure'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleFeeSubmit} className="space-y-4">
            {!editingFee && (
              <>
                {/* Nationality */}
                <div>
                  <Label>Nationality *</Label>
                  <Select
                    value={feeForm.nationality_id}
                    onValueChange={(value) =>
                      setFeeForm({ ...feeForm, nationality_id: value })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent>
                      {nationalities
                        .filter(n => n.is_active)
                        .map(nat => (
                          <SelectItem key={nat.id} value={nat.id}>
                            {nat.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Service Type */}
                <div>
                  <Label>Service Type *</Label>
                  <Select
                    value={feeForm.service_type_id}
                    onValueChange={(value) =>
                      setFeeForm({ ...feeForm, service_type_id: value })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes
                        .filter(t => t.is_active)
                        .map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Service */}
                <div>
                  <Label>Service *</Label>
                  <Select
                    value={feeForm.service_id}
                    onValueChange={(value) =>
                      setFeeForm({ ...feeForm, service_id: value })
                    }
                    required
                    disabled={!feeForm.service_type_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredServices
                        .filter(s => s.is_active)
                        .map(service => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sub Service */}
                {filteredSubServices.length > 0 && (
                  <div>
                    <Label>Sub-Service (Optional)</Label>
                    <Select
                      value={feeForm.sub_service_id}
                      onValueChange={(value) =>
                        setFeeForm({ ...feeForm, sub_service_id: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sub-service" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSubServices
                          .filter(ss => ss.is_active)
                          .map(ss => (
                            <SelectItem key={ss.id} value={ss.id}>
                              {ss.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* === LOCKED FEE HEADS === */}
            <div className="space-y-3">
              <div>
                <Label>Consular Fee *</Label>
                <Input
                  type="number"
                  min="0"
                  value={feeForm.fee_components.consular_fee}
                  onChange={(e) =>
                    setFeeForm({
                      ...feeForm,
                      fee_components: {
                        ...feeForm.fee_components,
                        consular_fee: Number(e.target.value) || 0
                      }
                    })
                  }
                  required
                />
              </div>

              <div>
                <Label>ICWF Fee *</Label>
                <Input
                  type="number"
                  min="0"
                  value={feeForm.fee_components.icwf_fee}
                  onChange={(e) =>
                    setFeeForm({
                      ...feeForm,
                      fee_components: {
                        ...feeForm.fee_components,
                        icwf_fee: Number(e.target.value) || 0
                      }
                    })
                  }
                  required
                />
              </div>

              <div>
                <Label>Service Fee *</Label>
                <Input
                  type="number"
                  min="0"
                  value={feeForm.fee_components.service_fee}
                  onChange={(e) =>
                    setFeeForm({
                      ...feeForm,
                      fee_components: {
                        ...feeForm.fee_components,
                        service_fee: Number(e.target.value) || 0
                      }
                    })
                  }
                  required
                />
              </div>
            </div>

            {/* Currency */}
            <div>
              <Label>Currency</Label>
              <Select
                value={feeForm.currency}
                onValueChange={(value) =>
                  setFeeForm({ ...feeForm, currency: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="KRW">KRW</SelectItem>
                  <SelectItem value="THB">THB</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="SGD">SGD</SelectItem>

                </SelectContent>
              </Select>
            </div>

            {/* Total */}
            <div className="bg-slate-100 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Fee (per passenger)</span>
                <span className="text-xl font-bold font-mono">
                  {feeForm.currency}{' '}
                  {(
                    feeForm.fee_components.consular_fee +
                    feeForm.fee_components.icwf_fee +
                    feeForm.fee_components.service_fee
                  ).toLocaleString()}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFeeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="save-fee-btn">
                {editingFee ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nationality Dialog */}
      <Dialog open={nationalityDialogOpen} onOpenChange={setNationalityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Nationality</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNationalitySubmit} className="space-y-4">
            <div>
              <Label htmlFor="nat-name">Country Name *</Label>
              <Input
                id="nat-name"
                value={nationalityForm.name}
                onChange={(e) => setNationalityForm({ ...nationalityForm, name: e.target.value })}
                placeholder="e.g., United States"
                required
              />
            </div>
            <div>
              <Label htmlFor="nat-code">Country Code *</Label>
              <Input
                id="nat-code"
                value={nationalityForm.code}
                onChange={(e) => setNationalityForm({ ...nationalityForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., US"
                maxLength={3}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNationalityDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Add Nationality</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFees;